import type { Status } from '../plan/arc';
import { statusVar } from '../plan/arc';

/**
 * The mark an arc's state wears. Same shapes as the phone's Studio tab —
 * that recognisability is the point, so they are not re-picked here.
 *
 * Colour comes from `--status*`, never from `heart` or `assistant` directly:
 * a status wearing another token's name is what made "lavender means synth"
 * false.
 */
export function StatusGlyph({
  status,
  size = 11,
  ink,
}: {
  status: Status;
  size?: number;
  /** A colour in place of the status's own, where the glyph leads words set
   *  in another ink and has to read as one with them. */
  ink?: string;
}) {
  const stroke = 1.4;
  const c = size / 2;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      style={{ color: ink ?? `var(${statusVar(status)})`, display: 'block' }}
    >
      {status === 'active' && (
        <>
          <circle cx={c} cy={c} r={c - stroke / 2} fill="none" stroke="currentColor" strokeWidth={stroke} />
          <circle cx={c} cy={c} r={c * 0.4} fill="currentColor" />
        </>
      )}
      {status === 'next' && (
        <circle cx={c} cy={c} r={c - stroke / 2} fill="none" stroke="currentColor" strokeWidth={stroke} />
      )}
      {status === 'needs-input' && (
        <>
          <path
            d={`M${c - c * 0.42} ${c * 0.42} A${c * 0.42} ${c * 0.42} 0 1 1 ${c} ${c * 0.98}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
          />
          <line
            x1={c}
            y1={c * 0.9}
            x2={c}
            y2={size * 0.66}
            stroke="currentColor"
            strokeWidth={stroke}
            strokeLinecap="round"
          />
          <circle cx={c} cy={size * 0.87} r={stroke * 0.62} fill="currentColor" />
        </>
      )}
      {status === 'parked' && (
        <>
          <rect x={c - c * 0.62} y={c * 0.3} width={stroke + 0.2} height={size * 0.7} rx={0.6} fill="currentColor" />
          <rect x={c + c * 0.12} y={c * 0.3} width={stroke + 0.2} height={size * 0.7} rx={0.6} fill="currentColor" />
        </>
      )}
      {status === 'done' && (
        <path
          d={`M${size * 0.2} ${size * 0.54} L${size * 0.42} ${size * 0.75} L${size * 0.82} ${size * 0.26}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke + 0.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

/** The doubled mark a storyline prefixes its title with. */
export function StorylineGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" aria-hidden="true" className="storyline-glyph">
      <circle cx="4.4" cy="6" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8.2" cy="6" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.55" />
    </svg>
  );
}

/**
 * What an archived arc wears in place of its status.
 *
 * In place of, not beside: filing something away supersedes what its status
 * was saying, and two marks in one lane would have the reader work out which
 * one is currently true. The status is still there in the file — that is what
 * makes the sweep reversible — it is just not what this row is about any more.
 *
 * A tray with a lid, at `inkFaint`: retired work should not compete with the
 * live rows around it.
 */
export function ArchivedGlyph({ size = 11 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      aria-hidden="true"
      style={{ color: 'var(--inkFaint)', display: 'block' }}
    >
      <rect x="1.2" y="1.6" width="9.6" height="2.6" rx="0.8" fill="currentColor" />
      <path
        d="M2.2 5.2h7.6v4.4a.8.8 0 0 1-.8.8H3a.8.8 0 0 1-.8-.8z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
      />
      <path d="M4.6 7.2h2.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}
